import { useFirebase } from '@chem-po/firebase-web'
import {
  AutoDisplayCondition,
  CONSENT_FORM_TEMPLATES,
  ConsentFormTemplate,
  MIDWIVES,
  SubmitAssessmentResultsArgs,
  TemplateId,
  WithId,
} from '@hb/shared'
import { collection, Query } from 'firebase/firestore'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { useQuery } from '../../../hooks/backend/useQuery'
import { useDefaultConsentForms } from '../hooks/useDefaultConsentForms'
interface DefaultConsentForms {
  practiceId: string | null
  hb: {
    form: WithId<ConsentFormTemplate>
    conditions: AutoDisplayCondition[]
  }[]
  practice: {
    form: WithId<ConsentFormTemplate>
    conditions: AutoDisplayCondition[]
  }[]
}
interface AssessmentResultsContextType {
  confirmingConsentFormsForResults: Omit<
    SubmitAssessmentResultsArgs,
    'consentFormIds' | 'viewableBeforeCall'
  > | null
  onConfirmAssessmentConfig: (
    consentFormIds: TemplateId[],
    viewableBeforeCall: boolean,
  ) => Promise<any>
  onCancelConsentFormsForResults: () => void
  defaultConsentForms: DefaultConsentForms
  loadingDefaultConsentForms: boolean
}

const AssessmentResultsContext = createContext<AssessmentResultsContextType | null>(null)

interface AssessmentResultsProviderProps {
  confirmingConsentFormsForResults: Omit<
    SubmitAssessmentResultsArgs,
    'consentFormIds' | 'viewableBeforeCall'
  > | null
  onConfirmAssessmentConfig: (
    consentFormIds: TemplateId[],
    viewableBeforeCall: boolean,
  ) => Promise<any>
  onCancelConsentFormsForResults: () => void
}

export const AssessmentResultsProvider = ({
  children,
  confirmingConsentFormsForResults,
  onConfirmAssessmentConfig,
  onCancelConsentFormsForResults,
}: PropsWithChildren<AssessmentResultsProviderProps>) => {
  const { firestore } = useFirebase()
  const {
    selectedAssessment: { snippetData, populated },
  } = useProfile()
  const { midwifeId: practiceId } = snippetData ?? {}

  const practiceConsentFormsQuery = useMemo(() => {
    if (!practiceId) return null
    return collection(firestore, `${MIDWIVES}/${practiceId}/consent-forms`) as Query<
      WithId<ConsentFormTemplate>
    >
  }, [practiceId, firestore])

  const hbConsentFormsQuery = useMemo(() => {
    if (!confirmingConsentFormsForResults) return null

    return collection(firestore, CONSENT_FORM_TEMPLATES) as Query<WithId<ConsentFormTemplate>>
  }, [confirmingConsentFormsForResults, firestore])

  const { data: hbConsentForms, loading: loadingHbConsentForms } = useQuery(hbConsentFormsQuery)
  const { data: practiceConsentForms, loading: loadingPracticeConsentForms } =
    useQuery(practiceConsentFormsQuery)

  const defaultConsentForms = useDefaultConsentForms(
    populated,
    practiceId,
    hbConsentForms,
    practiceConsentForms,
  )

  const loadingDefaultConsentForms = loadingHbConsentForms || loadingPracticeConsentForms

  return (
    <AssessmentResultsContext.Provider
      value={{
        confirmingConsentFormsForResults,
        onConfirmAssessmentConfig,
        onCancelConsentFormsForResults,
        defaultConsentForms,
        loadingDefaultConsentForms,
      }}>
      {children}
    </AssessmentResultsContext.Provider>
  )
}

export const useAssessmentResults = () => {
  const context = useContext(AssessmentResultsContext)
  if (!context) {
    throw new Error('useAssessmentResults must be used within an AssessmentResultsProvider')
  }
  return context
}
